/**
 * Live-transcription state for the phone's active call.
 *
 * Owned by `SoftphoneDock`'s top level, not by the call pane that renders it: collapsing the
 * dock unmounts every pane, and a hook living there would silently drop the SSE subscription —
 * and the transcript with it — each time the rep minimised the phone. The same trap, and the
 * same fix, as the source (tap-ucaas hoists it to its widget root for exactly this reason).
 *
 * The start response's `call_id` is the one subtlety worth knowing here. The tap goes on the
 * REP's leg — the uuid the console knows — but the transcript is keyed to the bridged LEAD leg
 * when there is one, and the server says which id it chose. Streaming the tapped uuid instead
 * would sit on a silent channel for every dialer call.
 *
 * Toggling off is best-effort, and ending the call sends no stop at all: the media bug lives
 * on the leg and dies with it, so there is nothing left to stop — the reset here is the UI
 * forgetting, not the platform acting.
 *
 * **On by default** (platform ADR-188): every call the rep is talking on is transcribed from the
 * moment it is answered, so the handoff note can be written from it. Once per call
 * (`shouldAutoTranscribe`): a rep who turns it off is not overruled for that call, a supervisor's
 * listen-in is left alone, and a deployment with no transcriber (a 503) is not told about on
 * every call.
 */

import * as React from "react";
import { api, streamEvents } from "@/lib/api";
import { currentMonitors } from "@/lib/monitor";
import {
  applyTranscriptEvent,
  clockTime,
  emptyFeed,
  parseTranscriptEvent,
  shouldAutoTranscribe,
  type SpeakerLabels,
  type TranscriptFeed,
  type TranscriptLine,
} from "@/softphone/transcription";
import type { Call } from "@/softphone/softphone-context";

interface TranscriptionStarted {
  status: string;
  channel: string;
  call_id: string;
  command_id: string;
}

export interface TranscriptionState {
  /** Whether the panel is live. Mirrors the tap as far as the console can know. */
  on: boolean;
  /** A toggle is in flight — the button disables rather than queueing a contradiction. */
  pending: boolean;
  error: string | null;
  finals: TranscriptLine[];
  partials: TranscriptLine[];
  toggle: () => void;
}

export function useTranscription(call: Call | null): TranscriptionState {
  const [on, setOn] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [feed, setFeed] = React.useState<TranscriptFeed>(emptyFeed);
  /** The id the server keyed the transcript to — what the stream subscribes on. */
  const [streamId, setStreamId] = React.useState<string | null>(null);

  const callId = call?.id ?? null;

  // The labels the reducer resolves speakers against. A ref, so a mid-call caller-id update
  // reaches the next line without tearing down and re-opening the SSE subscription.
  const labelsRef = React.useRef<SpeakerLabels>({ near: "You", far: null });
  React.useEffect(() => {
    labelsRef.current = { near: "You", far: call?.remoteName ?? call?.remote ?? null };
  }, [call?.remoteName, call?.remote]);

  // A new call is a new transcript. Render-time reset, same as the dock's own panes: an effect
  // would let one frame of the old call's lines render under the new call's number.
  const [seenCallId, setSeenCallId] = React.useState(callId);
  if (callId !== seenCallId) {
    setSeenCallId(callId);
    setOn(false);
    setPending(false);
    setError(null);
    setFeed(emptyFeed());
    setStreamId(null);
  }

  React.useEffect(() => {
    if (!on || !streamId) return;
    const handle = streamEvents(`/calls/${encodeURIComponent(streamId)}/transcript/stream`, {
      onData: (data) => {
        const event = parseTranscriptEvent(data);
        if (!event) return;
        setFeed((previous) =>
          applyTranscriptEvent(previous, event, labelsRef.current, {
            callId: streamId,
            at: clockTime(),
          }),
        );
      },
      onEnd: () => {
        // The server went away mid-call. Said plainly, and the toggle comes back up — the tap
        // itself may well still be running, and flipping it off/on renegotiates both halves.
        setError("The transcript stream dropped.");
        setOn(false);
      },
    });
    return () => handle.close();
  }, [on, streamId]);

  const start = React.useCallback(
    (auto: boolean) => {
      if (!callId) return;
      setPending(true);
      if (!auto) setError(null);
      void (async () => {
        try {
          const started = await api.post<TranscriptionStarted>(
            `/calls/${encodeURIComponent(callId)}/transcription/start`,
            { remote_number: call?.remote ?? undefined },
          );
          setFeed(emptyFeed());
          setStreamId(started.call_id);
          setOn(true);
        } catch {
          // Started by itself, it fails quietly: the rep did not ask, and a deployment with no
          // transcriber would otherwise say so on every call. The toggle still offers it.
          if (!auto) setError("Could not start transcription.");
        } finally {
          setPending(false);
        }
      })();
    },
    [callId, call?.remote],
  );

  // Every answered call, once (`shouldAutoTranscribe`). The calls already tried are a ref: a call
  // the rep turned off stays off, and a failed start is not retried on every render.
  const tried = React.useRef(new Set<string>());
  React.useEffect(() => {
    if (
      !call ||
      !shouldAutoTranscribe({
        state: call.state,
        on,
        pending,
        tried: tried.current.has(call.id),
        listenIn: currentMonitors().has(call.id),
      })
    ) {
      return;
    }
    tried.current.add(call.id);
    start(true);
  }, [call, on, pending, start]);

  const toggle = React.useCallback(() => {
    if (!callId || pending) return;
    tried.current.add(callId);
    if (!on) {
      start(false);
      return;
    }
    setOn(false);
    // Best-effort: a leg that already hung up took the bug with it, and trapping the rep in a
    // stuck "on" over a failed stop would be the worse outcome.
    void api.post(`/calls/${encodeURIComponent(callId)}/transcription/stop`).catch(() => undefined);
  }, [on, callId, pending, start]);

  return {
    on,
    pending,
    error,
    finals: feed.finals,
    partials: Object.values(feed.partials),
    toggle,
  };
}
